<script>
import {
  CdrButton,
  CdrLink,
  CdrText,
  CdrImg,
} from '@rei/cedar';
import LandingImageComponent from '@rei/landing-image-component';

export default {
  name: 'StorySlide',
  components: {
    CdrButton,
    CdrLink,
    CdrText,
    CdrImg,
    LandingImageComponent,
  },
  props: {
    cta: { type: Object, required: false, default: () => {} },
    media: { type: Object, required: false, default: () => {} },
    dynamicTabIndex: { type: Number, required: true, default: 0 },
    slideStyle: { type: String, required: false, default: 'Lifestyle 3:4 Small' },
    lastSlide: { type: Boolean, required: false, default: false },
    heading: { type: String, required: false, default: '' },
    greenBannerBoldText: { type: String, required: false, default: '' },
    price: { type: Number, required: false, default: 0 },
  },
  computed: {
    isLifestylePortrait() {
      return this.slideStyle.startsWith('Lifestyle 3:4');
    },
    isLifestyleSquare() {
      return this.slideStyle.startsWith('Lifestyle 1:1');
    },
    isLegacyMedia() {
      return !(this.media?.images?.xs || this.media?.images?.lg);
    },
    useSmallBtn() {
      return this.slideStyle === 'Lifestyle 3:4 Small';
    },
    getBtnSize() {
      return this.useSmallBtn ? 'small@xs small@sm small@md' : 'medium';
    },
    classStyle() {
      const styles = {
        'Lifestyle 3:4 Small': 'lifestyle-portrait-sm',
        'Lifestyle 3:4 Medium': 'lifestyle-portrait-md',
        'Lifestyle 3:4 Large': 'lifestyle-portrait',
        'Lifestyle 1:1': 'lifestyle-square',
      };
      return styles[this.slideStyle];
    },
    rewardAmountText() {
      return `Members get <strong>${this.greenBannerBoldText}</strong> in <br/> Co-op Member Reward`;
    },
  },
};
</script>

<template>
  <div
    class="slide"
    :class="[classStyle, { 'last-slide': lastSlide }]"
  >
    <template v-if="media">
      <landing-image-component
        v-if="!isLegacyMedia"
        v-bind="media"
        class="slide__image"
      />
      <cdr-img
        v-else
        :alt="media.alt"
        :src="media.src"
        class="slide__image"
        loading="lazy"
      />
    </template>
    <template v-if="cta">
      <div
        v-if="isLifestylePortrait"
        class="slide__button-container"
        :class="heading ? ['slide__button-container--heading'] : []"
      >
        <cdr-text
          v-if="heading"
          class="slide__heading"
        >
          {{ heading }}
        </cdr-text>
        <p
          class="slide__reward-amount-text"
          v-html="rewardAmountText"
        />
        <cdr-button
          class="slide__cta"
          :href="cta.target"
          :tabindex="dynamicTabIndex"
          tag="a"
          modifier="secondary"
          :data-analytics-id="`filmstrip ${cta.text}`"
          data-focus
          :size="getBtnSize"
        >
          {{ cta.text }}
        </cdr-button>
      </div>
      <cdr-link
        v-if="isLifestyleSquare"
        :href="cta.target"
        :tabindex="dynamicTabIndex"
        modifier="standalone"
        class="slide__link"
        data-focus
      >
        {{ cta.text }}
      </cdr-link>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import "../../../../style/filmstripMixins.scss";

.slide {
  $cdrContainer: calc((#{100vw} - 123.2rem) / 2 + #{$cdr-space-two-x});
  position: relative;
  background-color: $cdr-color-background-secondary;

  // Lifestyle Portrait (L)
  &.lifestyle-portrait {
    min-width: 259.5px;
    width: 350.5px;
    max-width: 350.5px;
    aspect-ratio: 3 / 4;
    border-radius: 8px;
    overflow: hidden;

    @include cdr-sm-mq-up {
      margin-right: $cdr-space-one-x;
      width: calc((100vw - 100px) / 3.1);
    }
    @include cdr-lg-mq-up {
      margin-right: $cdr-space-one-and-a-half-x;
    }

    .slide__image {
      margin-top: $cdr-space-four-x;
      mix-blend-mode: darken;
    }

    :deep(.landing-image-component__image) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  // Lifestyle Portrait (M)
  &.lifestyle-portrait-md {
    min-width: 209.5px;
    width: 209.5px;
    max-width: 308.5px;
    aspect-ratio: 3 / 4;
    border-radius: 8px;
    overflow: hidden;

    @include cdr-sm-mq-up {
      margin-right: $cdr-space-one-x;
      width: calc((100vw - 100px) / 3.1);
    }
    @include cdr-lg-mq-up {
      margin-right: $cdr-space-one-and-a-half-x;
    }

    :deep(.landing-image-component__image) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .slide__reward-amount-text {
      overflow: visible;
      text-wrap: nowrap;
      @include cdr-xs-mq-only {
        margin-top: 65%;
        margin-bottom: $cdr-space-half-x;
        padding-right: $cdr-space-half-x;
        margin-right: -12px;
      }
    }

    .slide__reward-amount-text,
    .slide__cta,
    .slide__heading {

      @include cdr-xs-mq-only {
        font-size: 1.3rem !important;
      }
      @include cdr-sm-mq-only {
        font-size: 1.4rem !important;
      }
    }

    .slide__button-container {
      @include cdr-md-mq-down {
        padding: $cdr-space-half-x;
      }
    }
  }

  // Lifestyle Portrait (S)
  &.lifestyle-portrait-sm {
    @extend .lifestyle-portrait;
    min-width: 162.5px;
    width: 162.5px;
    max-width: 259.5px;

    :deep(.slide__button-container) {
      padding: $cdr-space-three-quarter-x;

      @include cdr-lg-mq-up {
        padding: $cdr-space-one-x;
      }
    }
    @include cdr-sm-mq-up {
      margin-right: $cdr-space-three-quarter-x;
      width: calc((100vw - 100px) / 4.3);
    }
    @include cdr-lg-mq-up {
      margin-right: $cdr-space-one-x;
    }
  }

  // Lifestyle Square
  &.lifestyle-square {
    min-width: 224px;
    max-width: 355px;
    width: 224px;
    border-radius: 4px;

    @include cdr-sm-mq-up {
      margin-right: $cdr-space-one-x;
      width: calc((100vw - 100px) / 3.1);
    }
    @include cdr-lg-mq-up {
      margin-right: $cdr-space-one-and-a-half-x;
    }

    img {
      width: 100%;
      object-fit: cover;
      aspect-ratio: 1;
      border-radius: 4px;
    }

    :deep(.landing-image-component__image){
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
    }

    .slide__image, .landing-image-component {
      display: block;
      aspect-ratio: 1;
      padding: 0;
      margin: 0;
      line-height: 0;
      border-radius: 4px;
    }
  }

  &.last-slide {
    margin-right: $cdr-space-one-x;
    @include cdr-lg-mq-only {
      margin-right: calc($cdr-space-one-x + $cdrContainer);
    }
  }

  &__button-container {
    position: absolute;
    display: flex;
    align-items: flex-end;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    padding: $cdr-space-half-x;

    @include cdr-md-mq-up {
      padding: $cdr-space-one-and-a-half-x;
    }

    &--heading {
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
    }
  }

  &__heading {
    @include cdr-text-body-strong-300;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  &__reward-amount-text {
    @include cdr-text-body-300;
    line-height: 2rem;
    transform: rotate(5deg);
    background-color: $cdr-membership-vibrant-color-background-primary;
    padding:
      0.6rem
      $cdr-space-one-and-a-half-x
      0.6rem
      1rem;
    text-align: center;
    margin-top: 75%;
    margin-bottom: 0;
    align-self: flex-end;
    margin-right: -20px;

    @include cdr-sm-mq-up {
      margin-right: -30px;
    }
    @include cdr-sm-mq-only {
      margin-top: 65%;
    }
    @include cdr-md-mq-up {
      margin-right: -35px;
    }
  }

  &__cta {
    height: fit-content;
  }

  &__cta::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: transparent;
    z-index: 1;
  }

  &__link {
    @include link-tile-cover;
    @include cdr-text-heading-sans-400;
    color: $cdr-color-text-primary;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    margin-bottom: 0;
    width: 100%;
    order: 1;

    &:hover,
    &:focus,
    &:active {
      color: $cdr-color-text-primary;

      & ~ .filmstrip-carousel__slide-inner {
        box-shadow: inset 0 0 80px #ece6db;
        img {
          transform: scale(107%);
        }
      }
    }
  }
}
</style>
